import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Alert } from "react-bootstrap";

import axiosCustomerInstance from "../api/AxiosCustomerInstance";
import FloatingLabelInput from "../restaurant/PlaceHolder";
import styles from "../restaurant/css/SignIn.module.css";
import loginHero from "../restaurant/assets/maya-business-login-hero.png";
import MayaBrandMark from "../components/MayaBrandMark";
import { CUSTOMER_API_PATHS } from "../constants/apiPaths";
import { storeLoginTokens } from "../utils/sessionManager";

/** Canonical business login path (root admin + app users). */
export const EATS_BUSINESS_LOGIN_PATH = "/eats-business/login";

/** @deprecated Use EATS_BUSINESS_LOGIN_PATH — kept for redirects. */
export const EATS_APP_ADMIN_LOGIN_PATH = "/eats-business/admin/login";
/** @deprecated Use EATS_BUSINESS_LOGIN_PATH — kept for redirects. */
export const EATS_APP_USER_LOGIN_PATH = "/eats-app/user/login";

const EatsAppLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = { userName: identifier, email: identifier, password };

      let response = null;
      try {
        response = await axiosCustomerInstance.post(
          CUSTOMER_API_PATHS.eatsBusinessLogin,
          payload,
          { skipAuthRefresh: true }
        );
      } catch (err) {
        const baseURL = process.env.REACT_APP_API_CUSTOMER_URL || "http://127.0.0.1:5000";
        if (!err?.response) {
          setError(
            `Cannot reach customer API at ${baseURL}. Start eats-customer-api (port 5000) and restart the web app after changing .env.local.`
          );
        } else if (err.response.status === 404) {
          setError("Login API not found on configured server. Point frontend to updated customer API.");
        } else if (err.response.status === 401) {
          setError("Invalid credentials.");
        } else {
          setError(
            err.response.data?.message ||
              err.response.data?.error ||
              `Login failed (${err.response.status}). Check API URL and backend availability.`
          );
        }
        return;
      }

      const token =
        response.headers?.["x-auth-token"] ||
        response.headers?.["X-Auth-Token"] ||
        response.headers?.get?.("X-Auth-Token");
      const user = response.data?.user || {};

      if (!token) {
        setError("Login failed: auth token missing.");
        return;
      }

      storeLoginTokens(response);
      const firstName = String(user.firstName ?? user.first_name ?? "").trim();
      const lastName = String(user.lastName ?? user.last_name ?? "").trim();
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          userId: user.id ?? user.userId,
          firstName,
          lastName,
          name: `${firstName} ${lastName}`.trim() || user.name || user.userName,
          userRole: user.role,
          domain: "EATS_APP",
        })
      );
      if (typeof onLogin === "function") onLogin();
      navigate("/eats/admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.signin_container}>
      <div className={styles.signin_leftSection}>
        <img
          src={loginHero}
          alt="Eats and grocery business operations"
          className={styles.signin_hero}
        />
        <div className={styles.signin_leftOverlay}>
          <p className={styles.signin_leftEyebrow}>Operations for</p>
          <h1 className={styles.signin_leftTitle}>Food &amp; Grocery Business</h1>
          <p className={styles.signin_leftSubtitle}>
            Manage restaurants, stores, inventory, and orders from one platform.
          </p>
        </div>
      </div>

      <div className={styles.signin_rightSection}>
        <div className={styles.signin_form}>
          <Form onSubmit={handleSubmit}>
            <div className={styles.brandMarkSlot}>
              <MayaBrandMark variant="login" />
            </div>

            {error ? (
              <Alert variant="danger" style={{ marginBottom: "20px" }}>
                {error}
              </Alert>
            ) : null}

            <FloatingLabelInput
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              label="Enter Username"
              isRequired={true}
            />

            <br />
            <FloatingLabelInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Enter password"
              isRequired={true}
            />
            <br />

            <Button type="submit" disabled={loading} className={styles.button1}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EatsAppLogin;
